import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CpqData } from "../data/loader";
import { AnswersProvider } from "../state/AnswersContext";
import { RequiredFieldsProvider } from "../state/RequiredFields";
import { getLaunchContext } from "../util/launchContext";
import TabBar from "./TabBar";
import TabPanel from "./TabPanel";
import DebugPanel from "./DebugPanel";
import ValidationPanel from "./ValidationPanel";
import PricePanel from "./PricePanel";
import CompositeImagePanel from "./CompositeImagePanel";
import BurnerModelPanel from "./BurnerModelPanel";
import { getProfile } from "../products";
import SaveSubmitButton from "./SaveSubmitButton";
import AutoLoadItem from "./AutoLoadItem";
import ItemTemplateField from "./ItemTemplateField";
import AuditMenu from "./AuditMenu";
import ProgressBar from "./ProgressBar";
import FavoritesMenu from "./FavoritesMenu";
import ConfigModeBadge from "./ConfigModeBadge";
import PredictLaunch from "./PredictLaunch";
import PredictionRefresh from "./PredictionRefresh";
import SectionPrediction from "./SectionPrediction";
import PoBanner from "./PoBanner";
import { sectionModeOn } from "../util/sectionMode";
import PredictionToggle from "./PredictionToggle";
import RecentConfigs from "./RecentConfigs";
import { markReady, noteEvent, tabChanged } from "../util/sessionTrack";

export default function Configurator({ data }: { data: CpqData }) {
  const orderedTabs = useMemo(
    () =>
      [...data.tabs]
        // Hide the internal DEBUG tab from the configurator.
        .filter((t) => t.custrecord_cpqc_tab_code !== "DEBUG")
        .sort(
          (a, b) =>
            Number(a.custrecord_cpqc_tab_sequence || 0) -
            Number(b.custrecord_cpqc_tab_sequence || 0)
        ),
    [data.tabs]
  );

  const initialTab =
    orderedTabs.find(
      (t) =>
        t.custrecord_cpqc_tab_code ===
        data.product.fields.custrecord_cpqc_product_default_tab_hid
    )?.id ?? orderedTabs[0]?.id;

  const [activeTabId, setActiveTabId] = useState<string | undefined>(initialTab);

  const activeTab = orderedTabs.find((t) => t.id === activeTabId);
  const isDebugTab = activeTab?.custrecord_cpqc_tab_code === "DEBUG";

  // Save & Submit calls this with the first field that still needs an answer:
  // switch to its tab (so it's mounted) and scroll its red-highlighted box into
  // view. Without this the user can't tell what was flagged — the field is often
  // in a section below the fold or on another tab.
  const focusInvalidField = useCallback((tabId: string, code: string) => {
    setActiveTabId(tabId);
    // Poll briefly for the element: switching tabs mounts a new subtree, so it
    // may not exist on the next frame.
    let tries = 0;
    const tryScroll = () => {
      const el = document.querySelector<HTMLElement>(
        `.cpq-question.is-invalid[data-code="${code}"]`
      );
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      } else if (tries++ < 12) {
        requestAnimationFrame(tryScroll);
      }
    };
    requestAnimationFrame(tryScroll);
  }, []);

  // The progress strip's "Next:" button: same trip, to a field that is still needed rather
  // than one Save & Submit flagged, and it ends with the cursor in the field so the answer
  // can be typed or picked straight away.
  const jumpToField = useCallback((tabId: string, code: string) => {
    noteEvent("next-jump");
    setActiveTabId(tabId);
    let tries = 0;
    const tryJump = () => {
      const el = document.querySelector<HTMLElement>(`.cpq-question[data-code="${code}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.querySelector<HTMLElement>("input:not([type=hidden]), select, textarea")?.focus({ preventScroll: true });
      } else if (tries++ < 12) {
        requestAnimationFrame(tryJump);
      }
    };
    requestAnimationFrame(tryJump);
  }, []);

  // Manual tab switch: start the new tab at the TOP. The scroll container
  // (.cpq-main) keeps its offset across tab swaps, so a tall tab (e.g. Firetube)
  // would otherwise open scrolled down mid-page. focusInvalidField sets the tab
  // directly (not via this), so Save & Submit's scroll-to-flagged-field is unaffected.
  const mainRef = useRef<HTMLElement>(null);
  // Leaving the first page lets the silent page-2 prediction refresh land first (bounded),
  // so the next page is drawn with it rather than changing in front of the user.
  const leaveFirstPageRef = useRef<(() => Promise<void>) | null>(null);
  const handleSelectTab = useCallback(
    async (tabId: string) => {
      const leaving = activeTabId === orderedTabs[0]?.id && tabId !== activeTabId;
      if (leaving && leaveFirstPageRef.current) {
        await Promise.race([leaveFirstPageRef.current(), new Promise((r) => setTimeout(r, 1500))]);
      }
      setActiveTabId(tabId);
      requestAnimationFrame(() => {
        if (mainRef.current) mainRef.current.scrollTop = 0;
      });
    },
    [activeTabId, orderedTabs]
  );

  // Edit Config launches with an ?itemid= to preload. Hold the page behind a
  // loading screen until AutoLoadItem has applied that saved configuration, so
  // the user never sees an empty/default form flash before it repopulates.
  // (Nothing to preload -> ready immediately.)
  const [preloaded, setPreloaded] = useState<boolean>(() => !getLaunchContext().itemId);
  // A "Configure" launch holds the same way while its predicted answers are fetched and applied
  // (PredictLaunch), so the form appears already filled in grey rather than filling under the
  // user. An Edit Config launch is never predicted.
  const [predicting, setPredicting] = useState<boolean>(() => !getLaunchContext().itemId);
  useEffect(() => {
    if (!predicting) return;
    const t = window.setTimeout(() => setPredicting(false), 8000);
    return () => window.clearTimeout(t);
  }, [predicting]);
  const ready = preloaded && !predicting;
  // Safety net: if the preload stalls (e.g. a hung fetch), reveal the page anyway
  // after a bounded wait rather than trapping the user on the loading screen.
  useEffect(() => {
    if (preloaded) return;
    const t = window.setTimeout(() => setPreloaded(true), 15000);
    return () => window.clearTimeout(t);
  }, [preloaded]);

  // Where the time goes between opening and submitting (util/sessionTrack). Diagnostic only.
  const activeTabName = activeTab?.name ?? null;
  useEffect(() => {
    if (!ready) return;
    markReady({ product: data.slug, configMode: getLaunchContext().itemId ? "edit" : "new", tab: activeTabName });
    // Once, when the form is first usable; tab changes are tracked below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);
  useEffect(() => {
    tabChanged(activeTabName);
  }, [activeTabName]);

  // A product that can be drawn as a burner shows the live model; the illustration stays
  // for every other product, and for this one until a housing is chosen.
  const burnerModel = getProfile(data.slug).burnerModel;
  const illustration = (
    <CompositeImagePanel
      compositeImages={data.compositeImages}
      product={data.product}
      resources={data.resources}
      resourceFiles={data.resourceFiles}
    />
  );

  return (
    <AnswersProvider
      product={data.slug}
      questions={data.questions}
      qtables={data.qtables}
      operations={data.operations}
      materials={data.materials}
      tabs={data.tabs}
    >
      {/* Always mounted so it runs the preload; it reveals the page via onDone. */}
      <AutoLoadItem onDone={() => setPreloaded(true)} />
      <PredictLaunch onDone={() => setPredicting(false)} questions={data.questions} tabs={data.tabs} />
      <PredictionRefresh
        questions={data.questions}
        tabs={data.tabs}
        activeTabId={activeTabId}
        leaveRef={leaveFirstPageRef}
      />
      {sectionModeOn() && <SectionPrediction questions={data.questions} tabs={data.tabs} />}
      {!ready && (
        <div className="cpq-loading">
          <div className="cpq-spinner" />
          <p>{preloaded ? "Preparing your configuration…" : "Loading saved configuration…"}</p>
        </div>
      )}
      <RequiredFieldsProvider data={data}>
      <div className="cpq-root" data-product={data.slug} style={ready ? undefined : { display: "none" }}>
        <header className="cpq-header">
          <div className="cpq-brand">Flameco</div>
          <div className="cpq-product-title">
            {data.product.fields.altname}
          </div>
          <ConfigModeBadge />
          <PredictionToggle />
          {/* Item Template shortcut — moved to the header so it's available from
              any tab (was inline after the Order Type field). */}
          <ItemTemplateField compact />
          {/* Recent sits just left of Favorites (Kevin, 2026-10-19): the two menus that
              load a whole configuration, side by side. */}
          <RecentConfigs />
          <FavoritesMenu />
          <AuditMenu data={data} />
          <SaveSubmitButton data={data} onFocusInvalid={focusInvalidField} />
        </header>

        {/* Completion strip between the header and the tabs — always visible. */}
        <ProgressBar data={data} onJump={jumpToField} />
        <PoBanner />

        <TabBar
          tabs={orderedTabs}
          activeId={activeTabId}
          onSelect={handleSelectTab}
        />

        <div className={`cpq-body ${isDebugTab ? "is-debug" : ""}`}>
          <main className="cpq-main" ref={mainRef}>
            {activeTab ? (
              isDebugTab ? (
                <DebugPanel />
              ) : (
                <TabPanel tab={activeTab} questions={data.questions} firstTab={activeTab.id === orderedTabs[0]?.id} />
              )
            ) : (
              <p className="cpq-empty">No tabs available.</p>
            )}
          </main>

          {!isDebugTab && (
            <aside className="cpq-side">
              {burnerModel ? (
                <BurnerModelPanel burnerModel={burnerModel} fallback={illustration} />
              ) : (
                illustration
              )}
              <ValidationPanel
                validations={data.validations}
                questions={data.questions}
              />
              {/* Sales price last, at the bottom of the right column. */}
              <PricePanel />
            </aside>
          )}
        </div>
      </div>
      </RequiredFieldsProvider>
    </AnswersProvider>
  );
}
